import { z } from 'zod';
import {
  zBoxDefId,
  zChapterId,
  zI18nKey,
  zItemId,
  zItemKind,
  zItemLayer,
  zMaterial,
  zNpcId,
  zProductCategory,
  zRoomId,
  zSetId,
  zShopTab,
  zSurfaceTag,
  zThemeTag,
  zUnlockId,
  zWallFloorId,
  zZoneId,
} from './ids';
import { zRewards } from './progression';
import { zStoryGate } from './neighborhood';
import type { SurfaceTag, ZoneId } from '../ids';

// ---- homeTags (content-schemas §3.1) -----------------------------------------------------------

export type HomeTagAtom =
  | { kind: 'zone'; zone: ZoneId }
  | { kind: 'on'; surfaceTag: SurfaceTag }
  | { kind: 'near'; tag: string }
  | { kind: 'wall' }
  | { kind: 'wallAdjacent' };

/** All atoms must hold (AND). An item's `homeRules` array is OR. */
export interface HomeRule {
  all: readonly HomeTagAtom[];
}

export function parseHomeTag(raw: string): HomeRule {
  return {
    all: raw
      .split('+')
      .map((s) => s.trim())
      .map((atom): HomeTagAtom => {
        if (atom === 'wall') return { kind: 'wall' };
        if (atom === 'wall-adjacent') return { kind: 'wallAdjacent' };
        const sep = atom.indexOf(':');
        const k = sep < 0 ? atom : atom.slice(0, sep);
        const v = sep < 0 ? '' : atom.slice(sep + 1);
        if (!v) throw new Error(`Invalid homeTag "${raw}"`);
        if (k === 'zone') return { kind: 'zone', zone: zZoneId.parse(v) };
        if (k === 'on') return { kind: 'on', surfaceTag: zSurfaceTag.parse(v) };
        if (k === 'near') return { kind: 'near', tag: v };
        throw new Error(`Unsupported homeTag atom "${atom}"`);
      }),
  };
}

export const zHomeTags = z
  .array(z.string())
  .default([])
  .transform((arr, ctx) => {
    try {
      return arr.map(parseHomeTag);
    } catch (e) {
      ctx.addIssue({ code: 'custom', message: e instanceof Error ? e.message : String(e) });
      return z.NEVER;
    }
  });

// ---- Surface slots (content-schemas §3.2) ------------------------------------------------------

export const zAcceptClause = z
  .object({
    kinds: z.array(zItemKind).optional(),
    layers: z.array(zItemLayer).optional(),
    categories: z.array(zProductCategory).optional(),
    itemIds: z.array(zItemId).optional(),
    tags: z.array(z.string()).optional(),
  })
  .refine(
    (c) => Object.values(c).some((v: readonly unknown[] | undefined) => (v?.length ?? 0) > 0),
    'Empty accept clause',
  );
export type AcceptClause = z.output<typeof zAcceptClause>;

export const zSurfaceSlots = z
  .object({
    count: z.number().int().min(1).max(12),
    surfaceTag: zSurfaceTag,
    accepts: z.array(zAcceptClause).min(1),
    offsets: z.array(z.object({ x: z.number(), y: z.number() })).optional(),
    /** Slots inside the piece, under a board (a bookshelf's shelves). Lamps never go there: they sit on top. */
    inner: z.array(z.number().int().min(0)).optional(),
  })
  .refine((s) => !s.offsets || s.offsets.length === s.count, 'offsets must match count')
  .refine((s) => (s.inner ?? []).every((i) => i < s.count), 'inner slots must be below count')
  .refine((s) => (s.inner?.length ?? 0) < s.count, 'a piece with inner slots needs a top slot too');

// ---- ItemDef (content-schemas §3.3) ------------------------------------------------------------

export const zItemSource = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('catalog') }),
  z.object({ kind: z.literal('starter'), boxId: zBoxDefId }),
  z.object({ kind: z.literal('preplaced'), roomId: zRoomId, count: z.number().int().positive() }),
  z.object({ kind: z.literal('gift'), npcId: zNpcId, level: z.number().int().min(1) }),
  z.object({ kind: z.literal('story'), chapterId: zChapterId }),
  z.object({ kind: z.literal('reward'), ref: z.string().min(1) }),
]);

const zFootprint = z.object({ w: z.number().int().min(1).max(4), h: z.number().int().min(1).max(4) });

const PREFIX_KIND = { prod: 'product', fur: 'furniture', dec: 'decor', fix: 'fixture' } as const;

export const zItemDef = z
  .object({
    id: zItemId,
    kind: zItemKind,
    nameKey: zI18nKey,
    loreKey: zI18nKey,
    shopTab: zShopTab,
    productCategory: zProductCategory.optional(),
    layer: zItemLayer,
    footprint: zFootprint.optional(),
    wallSpan: z.union([z.literal(1), z.literal(2)]).optional(),
    rotations: z.union([z.literal(1), z.literal(2), z.literal(4)]),
    tags: z.array(z.string()).default([]),
    homeTags: zHomeTags,
    themeTags: z.array(zThemeTag).default([]),
    setId: zSetId.optional(),
    unlockId: zUnlockId.optional(),
    storyGate: zStoryGate.optional(),
    buyPrice: z.number().int().positive().optional(),
    sellPrice: z.number().int().positive().optional(),
    slots: zSurfaceSlots.optional(),
    removable: z.boolean().default(true),
    material: zMaterial,
    sources: z.array(zItemSource).min(1),
    sprite: z.object({
      frame: z.string().min(1),
      anchor: z.object({ x: z.number(), y: z.number() }).optional(),
    }),
    demandWeight: z.number().positive().optional(),
  })
  .superRefine((d, ctx) => {
    const prefix = d.id.slice(0, d.id.indexOf('_')) as keyof typeof PREFIX_KIND;
    if (PREFIX_KIND[prefix] !== d.kind)
      ctx.addIssue({ code: 'custom', message: 'kind does not match ID prefix' });
    if (d.kind === 'product') {
      if (
        !d.productCategory ||
        d.layer !== 'surface' ||
        d.buyPrice === undefined ||
        d.sellPrice === undefined
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'product needs productCategory, layer=surface, buyPrice, sellPrice',
        });
      } else if (d.sellPrice <= d.buyPrice) {
        ctx.addIssue({ code: 'custom', message: 'sellPrice must be greater than buyPrice' });
      }
    } else if (d.sellPrice !== undefined) {
      ctx.addIssue({ code: 'custom', message: 'only products have a sellPrice in the MVP' });
    }
    if ((d.layer === 'floor' || d.layer === 'object') && !d.footprint) {
      ctx.addIssue({ code: 'custom', message: 'floor/object items need a footprint' });
    }
    if (d.layer === 'wall' && !d.wallSpan)
      ctx.addIssue({ code: 'custom', message: 'wall items need wallSpan' });
    if (d.sources.some((s) => s.kind === 'catalog') && d.buyPrice === undefined) {
      ctx.addIssue({ code: 'custom', message: 'catalog source needs buyPrice' });
    }
  });

export type ItemDefInput = z.input<typeof zItemDef>;
export type ItemDef = z.output<typeof zItemDef>;

// ---- Sets & wall/floor (content-schemas §3.4–3.5) ----------------------------------------------

export const zSetDef = z.object({
  id: zSetId,
  nameKey: zI18nKey,
  itemIds: z.array(zItemId).min(2).max(12),
  completionRewards: zRewards,
  iconFrame: z.string(),
});
export type SetDefInput = z.input<typeof zSetDef>;
export type SetDef = z.output<typeof zSetDef>;

export const zWallFloorDef = z.object({
  id: zWallFloorId,
  kind: z.enum(['wall', 'floor']),
  nameKey: zI18nKey,
  price: z.number().int().positive().optional(),
  unlockId: zUnlockId.optional(),
  storyGate: zStoryGate.optional(),
  isDefault: z.boolean(),
  texture: z.string(),
  themeTags: z.array(zThemeTag).default([]),
});
export type WallFloorDefInput = z.input<typeof zWallFloorDef>;
export type WallFloorDef = z.output<typeof zWallFloorDef>;
